import { useState, useEffect } from 'react'
import { useApolloClient } from '@apollo/client'
import {
  MaterialPricingAndAvailabilityQuery,
  MaterialPricingAndAvailabilityDocument,
} from '@src/queries/MaterialPricingAndAvailabilityQuery.generated'
import {
  DiscontinuedMaterialPricingDetailFragment,
  ValidMaterialPricingDetailFragment,
} from '@src/fragments/ProductPricing.generated'
import { fromPairs } from 'lodash'
import { CartItemType } from '@src/types/graphql-types'
import { QuickCartItemFragment } from '@src/queries/AllCartsQuery.generated'
import { CartType } from '@src/routes'

interface QueryResult<T1, T2> {
  materialPriceAvailability?: T1
  discontinuedPricingInfo?: T2
  loading: boolean
  error?: any
}

const useCartItemsPriceAvailability = (
  cartItems: QuickCartItemFragment[],
  cartType?: CartType,
  limit?: number
) => {
  const client = useApolloClient()
  const [data, setData] = useState<
    Partial<{
      [id: string]: QueryResult<
        ValidMaterialPricingDetailFragment | null,
        DiscontinuedMaterialPricingDetailFragment | null
      >
    }>
  >({})

  // loading and error states for each item
  const loading = cartItems?.some((item) => data[item.id]?.loading)
  const error = cartItems?.some((item) => data[item.id]?.error)

  // calculate subtotal and currency
  const subTotal: number | null = !loading
    ? Object.keys(data).reduce((acc, cur) => {
        return acc + (data[cur]?.materialPriceAvailability?.netPrice || 0)
      }, 0)
    : null

  const currency = Object.values(data)
    .map((item) => item?.materialPriceAvailability?.currency)
    .find(Boolean)

  // filter items using limit and cart type
  const filterItems = (items: QuickCartItemFragment[]) => {
    return items
      .filter(
        ({ type }) =>
          type === CartItemType.Prepack ||
          type === CartItemType.ThirdPartyProvider
      )
      .slice(0, limit)
  }

  const getCatalogType = (
    item: QuickCartItemFragment,
    cartType: CartType | undefined
  ) => {
    if (item?.type === CartItemType.ThirdPartyProvider) {
      return 'buildingblocks'
    }
    if (cartType === 'marketplacecart') {
      return 'marketplace'
    }
    return undefined
  }

  useEffect(() => {
    if (!cartItems?.length) return

    const fetchPricing = async () => {
      setData((data) => {
        return fromPairs(
          filterItems(cartItems).map(({ id }) => {
            return [
              id,
              {
                loading: true,
                materialPriceAvailability: data[id]?.materialPriceAvailability,
                discontinuedPricingInfo: data[id]?.discontinuedPricingInfo,
                error: data[id]?.error,
              },
            ]
          })
        )
      })
      await Promise.all(
        filterItems(cartItems).map(async (item) => {
          try {
            const { data } =
              await client.query<MaterialPricingAndAvailabilityQuery>({
                query: MaterialPricingAndAvailabilityDocument,
                variables: {
                  materialNumber: item.material.number,
                  quantity: item.quantity,
                  catalogType: getCatalogType(item, cartType),
                },
              })

            setData((pricing) => ({
              ...pricing,
              [item.id]: {
                materialPriceAvailability:
                  data?.getPricingForMaterial?.materialPricing,
                discontinuedPricingInfo:
                  data?.getPricingForMaterial?.discontinuedPricingInfo,
                loading: false,
              },
            }))
          } catch (err) {
            setData((pricing) => ({
              ...pricing,
              [item.id]: {
                loading: false,
                materialPriceAvailability: undefined,
                discontinuedPricingInfo: undefined,
                error: err,
              },
            }))
          }
        })
      )
    }

    fetchPricing()
  }, [cartItems])

  return { data, loading, error, subTotal, currency }
}

export default useCartItemsPriceAvailability
